import { useCallback, useState } from 'react'
import resourceUriDecorate, { RequiredWidth } from '~utils/resource-uri-decorator'

import Overlay from '~components/root/overlay'
import Portal from '~components/root/portal'
import Dialog, { DIALOG_TRANSITION_DELAY } from '~components/ui/dialog'
import Icon from '~components/ui/icon'
import { getMediaType, getVideoInfo } from '~components/ui/media'
import useBlockScroll from '~hooks/use-block-scroll'
import useEscape from '~hooks/use-escape'

type VideoProps = Readonly<{
  url: string
  onErrorCapture: () => void
}>

type MediaModalProps = Readonly<{
  url: string
  totalMediaCount: number
  focusedIndex: number | null
  onPrevClick: () => void
  onNextClick: () => void
  onRequestClose: () => void
}>

const Video = ({ url, onErrorCapture }: VideoProps) => {
  const { videoUrl, videoThumbnailUrl } = getVideoInfo(url)

  return (
    <video
      autoPlay
      playsInline
      controls
      src={videoUrl}
      poster={videoThumbnailUrl}
      onErrorCapture={onErrorCapture}
      className="h-full w-full"
    >
      <source src={videoUrl} />
      <track kind="captions" />
    </video>
  )
}

const MediaModal = ({
  url,
  totalMediaCount,
  focusedIndex,
  onPrevClick,
  onNextClick,
  onRequestClose,
}: MediaModalProps) => {
  const [isVideoError, setVideoError] = useState(false)

  const onErrorCapture = useCallback(() => {
    setVideoError(true)
  }, [])

  const onErrorReset = useCallback(() => {
    onRequestClose()
    setTimeout(() => setVideoError(false), DIALOG_TRANSITION_DELAY)
  }, [onRequestClose])

  useEscape({
    callback: onRequestClose,
  })

  useBlockScroll(true)

  if (isVideoError) {
    return (
      <Dialog type="alert" open>
        <Dialog.Title>Still processing</Dialog.Title>
        <Dialog.Content>We're processing this video. Please check back later.</Dialog.Content>
        <Dialog.Action>
          <Dialog.TextButton priority="primary" onClick={onErrorReset}>
            Confirm
          </Dialog.TextButton>
        </Dialog.Action>
      </Dialog>
    )
  }

  if (!url || focusedIndex === null) {
    return <></>
  }

  return (
    <Portal id="modal">
      <Overlay open className="flex flex-col justify-between">
        <div className="h-[75px] w-full border-b border-solid border-gray-400">
          <button
            aria-label="close-button"
            type="button"
            onClick={onRequestClose}
            className="absolute right-3 top-3 z-10 p-2"
          >
            <Icon name="close" className="size-8 fill-white" strokeLinecap="round" />
          </button>
        </div>
        {getMediaType(url) === 'video' ? (
          <div className="relative m-auto h-3/4 w-auto">
            <Video url={url} onErrorCapture={onErrorCapture} />
          </div>
        ) : (
          <div className="relative m-auto h-3/4 w-full">
            <img
              src={resourceUriDecorate(url, {
                width: RequiredWidth.W1080,
              })}
              className="absolute inset-0 h-full w-full"
              alt="photo"
              style={{
                objectFit: 'contain',
              }}
            />
          </div>
        )}
        <div className="flex h-[75px] w-full items-center justify-center border-t border-solid border-gray-400 text-white">
          <button aria-label="previous-button" className="p-2" onClick={onPrevClick}>
            <Icon name="chevron_right" className="h-6 w-6 rotate-180 fill-white" />
          </button>
          <div className="w-20 text-center text-title-3">
            {`${focusedIndex + 1} of ${totalMediaCount}`}
          </div>
          <button aria-label="next-button" className="p-2" onClick={onNextClick}>
            <Icon name="chevron_right" className="h-6 w-6 fill-white" />
          </button>
        </div>
      </Overlay>
    </Portal>
  )
}

export default MediaModal
